import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs, Select, Button, Modal } from "antd";
import {
  ArrowLeftOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { Row } from "components/lib";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { JobBasicData } from "types/job-basic-data";
import { Employer } from "../../employer/employer-list/list";
import { Job } from "../all-jobs/list";
import { create } from "domain";
import {
  changeArrayToWeekDay,
  taskStatus,
  workingPeriodStatus,
} from "../../../utils/common";
import moment from "moment";
import { useEditJob } from "../../../utils/job";

const { Option } = Select;

interface TopBarDat extends Partial<Job> {
  activeTab?: string;
  selectedHelpersLength?: number;
  tabChange: (key: string) => void;
  cancelledJob: () => void;
}

export const TopBar = (props: TopBarDat) => {
  const navigate = useNavigate();
  const {
    taskNo,
    taskHelperRequirements,
    workingHours,
    status,
    workingPeriod,
    personInCharge,
    createdAt,
    activeTab,
    endDate,
    address,
    startDate,
    startTime,
    week,
    district,
    selectedHelpersLength = 0,
    cancelledJob,
    tabChange,
  } = props;

  const { id } = useParams();

  // combine to one line after add variables
  const jobInvitation = `
    *UniCare360 job offers*\n
    ⏰ *job number:* ${taskNo}
    ⏰ *type of work:* ${taskHelperRequirements?.map(item => item.position ).filter(item => item !== "self priced prices").join(", ")}
    ⏰ *job period:* ${workingPeriodStatus(workingPeriod)}
    🗓 *start date:* ${moment(startDate).format("YYYY-MM-DD")}
    ${
      workingPeriod === 0 &&
      `🗓 *end date:* ${moment(endDate).format("YYYY-MM-DD")}`
    }
    ⏰ *working hours:* ${moment(startTime, "HH:mm:ss").format("HH:mm")} - ${moment(startTime, "HH:mm:ss").add(workingHours, "h").format("HH:mm")}
    ${week && ` 🗓 *period:* ${changeArrayToWeekDay(week || [])}`}
    *job address:* ${district}\n
    *For enquiries, please call /whatsApps：98675630, if you don t have time to pick up the above jobs, Please take the trouble to forward it to interested friends/colleagues, thank you🙇‍♀‍\n
    ✅ *apply for a job* ✅
    https://au.360unicare.com/applyjob/${id}`;

  const jobConfirm = `
    *UniCare360 job confirmation and check in*\n
    ⏰ *job number:* ${taskNo}
    ⏰ *job period:* ${workingPeriodStatus(workingPeriod)}
    🗓 *start date:* ${moment(startDate).format("YYYY-MM-DD")}
    ⏰ *working hours:* ${moment(startTime, "HH:mm:ss").format("HH:mm")} - ${moment(startTime, "HH:mm:ss").add(workingHours, "h").format("HH:mm")}
    *job address:* ${district} - ${address}\n
    *For enquiries, please call/whatsApps：98675630‍\n
    ✅ *Confirmation of work* ✅
    https://360unicare.com/checkoutjob/${id}`;

  return (
    <Container>
      <FixedBar between={true}>
        <BarLeft gap={true}>
          <ArrowLeftOutlined onClick={() => navigate(-1)} />
          <Title>{taskNo}</Title>
          <Status>{taskStatus(status)}</Status>
          <Frequency>{workingPeriodStatus(workingPeriod)}</Frequency>
          <AdminName>Created by: {personInCharge?.username}</AdminName>
          <CreateDate>
            Date of creation: {moment(createdAt).format("YYYY-MM-DD HH:mm")}
          </CreateDate>
        </BarLeft>
        {(status === 0 || status === 1 || status === 2) && (
          <BarRight>
            {selectedHelpersLength > 0 ? (
              <CopyToClipboard text={jobConfirm} onCopy={() => alert("copied")}>
                <Button style={{ marginRight: 16 }}>
                  <CopyOutlined />
                  Copy job confirmations and check-ins
                </Button>
              </CopyToClipboard>
            ) : (
              <CopyToClipboard
                text={jobInvitation}
                onCopy={() => alert("copied")}
              >
                <Button style={{ marginRight: 16 }}>
                  <CopyOutlined />
                  Copy the job offer
                </Button>
              </CopyToClipboard>
            )}
            <Button onClick={() => cancelledJob()}>Cancel work</Button>
          </BarRight>
        )}
      </FixedBar>
      <TabContainer>
        <Tabs activeKey={activeTab} onChange={(key) => tabChange(key)}>
          <Tabs.TabPane tab="Details of the job" key="1"></Tabs.TabPane>
          <Tabs.TabPane tab="Job records" key="2"></Tabs.TabPane>
        </Tabs>
      </TabContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 122px;
  padding: 28px 32px;
  background-color: #ffffff;
  position: sticky;
  top: 0px;
  z-index: 999;
`;

const FixedBar = styled(Row)`
  z-index: 1;
`;
const BarLeft = styled(Row)``;
const BarRight = styled(Row)``;

const Title = styled.div`
  color: #3c3e42;
  font-size: 20px;
  font-weight: bold;
`;

const Status = styled.div`
  background-color: #d1f3f8;
  padding: 5px 10px;
  color: #2498aa;
  font-size: 12px;
  font-weight: bold;
  border-radius: 8px;
`;

const Frequency = styled.div`
  background-color: #eff2f8;
  padding: 5px 10px;
  color: #676b73;
  font-size: 12px;
  font-weight: bold;
  border-radius: 3px;
`;

const AdminName = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
`;

const CreateDate = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
`;

const TabContainer = styled.div`
  padding-top: 21px;
`;
